import React, { useCallback, useState, useRef } from 'react';
import { useForm } from "react-hook-form";
import ReCAPTCHA from "react-google-recaptcha";
import { Button } from "core_src/components";
import { AnvilInput } from "frontend/form/components";
import { frontendSettings, templateSettings } from 'anvil_settings';
import { coreUrls } from "core_src/js/core_urls_config";
import { observer } from "mobx-react-lite";
import { get } from "lodash";

import { PaywallCard } from "./PaywallCard";
import axios from "axios";
import { usePaywallStoreContext } from "./Store";
import { analytics } from "anvil-analytics/analytics";
import { socialConfig } from "anvil_social_src/social_config";
import { SocialLogin } from "frontend/social/components/SocialLogin";
import { RichText } from "frontend/wagtail/components/RichText";


/**
 * Component for displaying the registration wall that prompts a user for their email
 * before allowing them to read more articles.
 *
 * @param {string} pagePk - The primary key of the page being blocked.
 * @param {string} pageUrl - URL to direct to after registering
 * @param {string} termsOfUseUrl - The URL of the Terms Of Service page.
 * @param {string} privacyPolicyUrl - The URL of the Privacy Policy page.
 * @returns {*}
 * @constructor
 */
export const RegistrationWall = observer(function RegistrationWall({pagePk, pageUrl, termsOfUseUrl, privacyPolicyUrl}) {
    const recaptchaRef = useRef(null);
    const {
        handleSubmit,
        setError,
        control,
    } = useForm();
    const paywallStore = usePaywallStoreContext();

    const socialNextUrl = `${coreUrls.registration_wall_social_submit}?page=${pagePk}`;
    const appleMethod = 'AppleID';
    const facebookMethod = 'Facebook';

    const appleNextUrl = `${socialNextUrl}&login_method=${appleMethod}`;
    const facebookNextUrl = `${socialNextUrl}&login_method=${facebookMethod}`;

    const socialLoginEnabled = socialConfig.apple.enabled || socialConfig.facebook.enabled;

    const [showLoader, setLoader] = useState(false);

    let footerContent = <>
        By creating an account, you agree to the
        <a href={termsOfUseUrl}> Terms of Service</a> and acknowledge our
        <a href={privacyPolicyUrl}> Privacy Policy</a>. This
        site is protected by reCAPTCHA and the Google
        <a href="https://policies.google.com/privacy"> Privacy Policy</a> and
        <a href="https://policies.google.com/terms"> Terms of Service</a> apply.
    </>;

    const onSubmit = useCallback(async (values) => {
        setLoader(true);

        let captchaToken;
        try {
            captchaToken = await recaptchaRef.current.executeAsync();
        } catch (error) {
            setLoader(false);
            setError("email", {
                type: "captchaError",
                message: "An unknown error has occurred. Please try again."
            });
            return;
        }

        const userId = analytics.user('userId');

        try {
            await axios.post(coreUrls.registration_wall_submit, {
                email: values.email,
                page: pagePk,
                recaptcha: captchaToken,
                analytics_user_id: userId
            });
            paywallStore.registrationWall.registeredEmail = values.email;
        } catch (error) {
            setLoader(false);
            // Reset the recaptcha field so that when they try again, we can compute a new token
            recaptchaRef.current.reset();
            let msg = get(error, 'response.data.detail',
                'Oops. We had a problem setting you up. Hang on a second and try again.');
            setError("email", {
                type: "backend",
                // The error message from the server can contain links
                message: <span dangerouslySetInnerHTML={{__html: msg}} />,
            });
            // Track the form error
            analytics.track('FormSubmitInvalid', {
                page: pageUrl,
                category: 'Order Tunnel',
                errors: {'registration_wall_email': [msg]},
            });
            return;
        }

        // Track successful registration wall submissions
        analytics.track('SubmitRegistrationWall', {
            page: pageUrl,
            method: 'Direct',
            email: values.email,
        });

        setLoader(false)

    }, [pagePk, pageUrl, paywallStore.registrationWall, setError]);

    if (paywallStore.registrationWall.hasRegistered) {
        return (
            <Thanks email={paywallStore.registrationWall.registeredEmail}/>
        )
    }

    return (
        <PaywallCard header={templateSettings.paywall_registration_wall_header} footer={footerContent} loginNextUrl={pageUrl}>
            <RichText className="registration-wall__prompt"
                 data-visibility-tracker="DisplayRegistrationWall"
                 data-visibility-tracker-one-shot={true}
                 data-visibility-tracker-arg-page={pageUrl}
                 data={templateSettings.paywall_registration_wall_text}/>
            <form onSubmit={handleSubmit(onSubmit)} className="registration-wall__form">
                <AnvilInput name="email"
                            control={control}
                            labelText="Email Address"
                            isRequired={true}
                            className="registration-wall__input"
                            inputProps={{type: 'email'}}
                />
                <ReCAPTCHA
                    ref={recaptchaRef}
                    size="invisible"
                    sitekey={frontendSettings.recaptcha_public_key}
                />
                <Button text="Continue Reading" showLoader={showLoader} fill={true}
                        className="registration-wall__button"
                        successColor={true}/>

                {socialLoginEnabled && (
                    <div className="registration-wall__divider" data-divider-message="Or use your social">
                    </div>
                )}
                <SocialLogin
                    facebookNext={facebookNextUrl}
                    facebookButtonProps={{
                        "data-click-tracker": "SubmitRegistrationWall",
                        "data-click-tracker-arg-page": pageUrl,
                        "data-click-tracker-arg-method": facebookMethod,
                    }}
                    appleNext={appleNextUrl}
                    appleButtonProps={{
                        "data-click-tracker": "SubmitRegistrationWall",
                        "data-click-tracker-arg-page": pageUrl,
                        "data-click-tracker-arg-method": appleMethod,
                    }}

                />
            </form>
        </PaywallCard>
    )
})

/**
 * Component to display a thanks message after the user has given us their email.
 * @param email - Provided email address
 * @returns {*}
 * @constructor
 */
export function Thanks({email}) {
    const paywallStore = usePaywallStoreContext();

    const changeEmail = useCallback(() => {
        paywallStore.registrationWall.registeredEmail = null;
    }, [paywallStore.registrationWall]);
    return (
        <PaywallCard header={templateSettings.paywall_registration_wall_thanks_header}>
            <p>We&apos;ve sent an email to <strong>{email}</strong> <a onClick={changeEmail}>(change)</a>.</p>
            <RichText data={templateSettings.paywall_registration_wall_thanks_text}/>
        </PaywallCard>
    );
}
